import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card, CardHeader, CardBody, CardFooter, CardTitle,
  Row, Col, Table, Form, FormGroup, Input, Button, Label
} from "reactstrap";

import { API_BASE_URL } from "../config/api";

const BASE_URL = API_BASE_URL;

function GameQuestions() {
  const [lang, setLang] = useState("en");
  const [levels, setLevels] = useState([]); // [{id,name,label,label_en,label_ar,points_per_question,min_score}]
  const [levelsError, setLevelsError] = useState("");
  const [hallOptionsByLevel, setHallOptionsByLevel] = useState({});
  const [hallsError, setHallsError] = useState("");

  const [selectedLevel, setSelectedLevel] = useState(""); // قيم إنجليزية
  const [questions, setQuestions] = useState([]);
  const [qLoading, setQLoading] = useState(false);
  const [qError, setQError] = useState("");
  const [qSuccess, setQSuccess] = useState("");

  // إضافة سؤال جديد
  const [addQError, setAddQError] = useState("");
  const [addQSuccess, setAddQSuccess] = useState("");
  const [newQFieldErrors, setNewQFieldErrors] = useState({
    correct_answer: "",
    correct_answer_ar: "",
  });
  const [newQ, setNewQ] = useState({
    // EN
    question: "",
    option_a: "",
    option_b: "",
    option_c: "",
    correct_answer: "",
    hint: "",
    level: "",
    hall: "", // نص (إنجليزي/أو اسم القاعة بالإنجليزي)
    // AR (اختياري)
    question_ar: "",
    option_a_ar: "",
    option_b_ar: "",
    option_c_ar: "",
    correct_answer_ar: "",
    hint_ar: "",
    hall_ar: "" // 👈 جديد: اسم القاعة بالعربي
  });

  // تحديث سؤال
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState("");
  const [updateQ, setUpdateQ] = useState({
    // EN
    question: "",
    option_a: "",
    option_b: "",
    option_c: "",
    correct_answer: "",
    hint: "",
    // AR
    question_ar: "",
    option_a_ar: "",
    option_b_ar: "",
    option_c_ar: "",
    correct_answer_ar: "",
    hint_ar: "",
    // مشتركة
    level: "",
    hall: "",     // نص (إنجليزي)
    hall_ar: ""   // 👈 جديد: اسم القاعة بالعربي
  });

  const notify = (setter, msg, t = 4000) => {
    setter(msg);
    setTimeout(() => setter(""), t);
  };

  // -------- API --------
  const fetchLevels = async () => {
    try {
      setLevelsError("");
      const res = await axios.get(`${BASE_URL}/game/levels?all=1`, {
        headers: { "Accept-Language": lang === "ar" ? "ar" : "en" }
      });
      setLevels(res.data || []);
    } catch {
      setLevelsError("Error fetching levels.");
    }
  };

  const fetchQuestions = async (level) => {
    if (!level) {
      notify(setQError, "Choose a level first.");
      return;
    }
    try {
      setQError("");
      setQLoading(true);
      const res = await axios.get(
        `${BASE_URL}/game/questions/all/${encodeURIComponent(level)}`,
        { headers: { "Accept-Language": lang === "ar" ? "ar" : "en" } }
      );
      setQuestions(res.data || []);
    } catch {
      notify(setQError, "Error fetching questions.");
    } finally {
      setQLoading(false);
    }
  };

  const fetchHallsByLevel = async (level, { force = false } = {}) => {
    if (!level) {
      return [];
    }

    if (!force && hallOptionsByLevel[level]) {
      return hallOptionsByLevel[level];
    }

    try {
      setHallsError("");
      const res = await axios.get(`${BASE_URL}/game/halls/order/${encodeURIComponent(level)}`);
      const list = (res.data?.data || [])
        .map((hallItem, index) => ({
          hall: hallItem.hall,
          hall_ar: hallItem.hall_ar || "",
          sort_order: Number.isFinite(hallItem.sort_order) ? hallItem.sort_order : index + 1,
        }))
        .filter((hallItem) => hallItem.hall)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

      setHallOptionsByLevel((prev) => ({ ...prev, [level]: list }));
      return list;
    } catch (error) {
      console.error(error);
      notify(setHallsError, "Error fetching halls for the selected level.");
      return [];
    }
  };

  const getHallOptions = (level, currentHall = "", currentHallAr = "") => {
    const cachedOptions = hallOptionsByLevel[level] || [];
    if (!currentHall) {
      return cachedOptions;
    }
    if (cachedOptions.some((hallItem) => hallItem.hall === currentHall)) {
      return cachedOptions;
    }
    return [
      ...cachedOptions,
      {
        hall: currentHall,
        hall_ar: currentHallAr || "",
        sort_order: cachedOptions.length + 1,
      },
    ];
  };

  const getHallLabel = (hallItem) => {
    if (!hallItem) return "";
    return hallItem.hall || "";
  };

  const handleNewLevelChange = async (level) => {
    setNewQ((prev) => ({ ...prev, level, hall: "", hall_ar: "" }));
    if (level) {
      await fetchHallsByLevel(level);
    }
  };

  const handleNewHallChange = (hallName) => {
    const selectedHall = getHallOptions(newQ.level, newQ.hall, newQ.hall_ar)
      .find((hallItem) => hallItem.hall === hallName);
    setNewQ((prev) => ({
      ...prev,
      hall: hallName,
      hall_ar: selectedHall?.hall_ar || "",
    }));
  };

  const handleUpdateLevelChange = async (level) => {
    setUpdateQ((prev) => ({ ...prev, level, hall: "", hall_ar: "" }));
    if (level) {
      await fetchHallsByLevel(level);
    }
  };

  const handleUpdateHallChange = (hallName) => {
    const selectedHall = getHallOptions(updateQ.level, updateQ.hall, updateQ.hall_ar)
      .find((hallItem) => hallItem.hall === hallName);
    setUpdateQ((prev) => ({
      ...prev,
      hall: hallName,
      hall_ar: selectedHall?.hall_ar || "",
    }));
  };

  const submitNewQuestion = async (e) => {
    e.preventDefault();
    setNewQFieldErrors({
      correct_answer: "",
      correct_answer_ar: "",
    });

    const req = ["question","option_a","option_b","option_c","hint","level","hall","hint_ar"];
    for (const k of req) {
      const value = newQ[k];
      const isEmpty = typeof value === "string" ? !value.trim() : !value;
      if (isEmpty) {
        notify(setAddQError, "All fields are required.");
        return;
      }
    }

    const normalizedCorrectAnswer = String(newQ.correct_answer || "").trim().toUpperCase();
    const normalizedCorrectAnswerAr = String(newQ.correct_answer_ar || "").trim();

    const nextFieldErrors = {
      correct_answer: "",
      correct_answer_ar: "",
    };

    if (!normalizedCorrectAnswer || !["A","B","C"].includes(normalizedCorrectAnswer)) {
      nextFieldErrors.correct_answer = "Please enter one of: A / B / C";
    }
    if (!normalizedCorrectAnswerAr || !["أ","ب","ج"].includes(normalizedCorrectAnswerAr)) {
      nextFieldErrors.correct_answer_ar = "يرجى إدخال أحد القيم: أ / ب / ج";
    }

    if (nextFieldErrors.correct_answer || nextFieldErrors.correct_answer_ar) {
      setNewQFieldErrors(nextFieldErrors);
      return;
    }

    const hallName = (newQ.hall || "").trim();
    if (!hallName) {
      notify(setAddQError, "Hall is required.");
      return;
    }

    try {
      const payload = {
        // EN
        question: newQ.question,
        option_a: newQ.option_a,
        option_b: newQ.option_b,
        option_c: newQ.option_c,
        correct_answer: normalizedCorrectAnswer,
        hint: newQ.hint,
        level: newQ.level,
        hall: hallName,
        // AR اختياري
        question_ar: newQ.question_ar ?? "",
        option_a_ar: newQ.option_a_ar ?? "",
        option_b_ar: newQ.option_b_ar ?? "",
        option_c_ar: newQ.option_c_ar ?? "",
        correct_answer_ar: normalizedCorrectAnswerAr,
        hint_ar: newQ.hint_ar ?? "",
        hall_ar: (newQ.hall_ar ?? "").toString().trim() // 👈 نمرّره دومًا كسلسلة
      };

      const res = await axios.post(`${BASE_URL}/game/questions/add`, payload);
      if (res.status === 201) {
        notify(setAddQSuccess, "Question added successfully.");
        setNewQFieldErrors({
          correct_answer: "",
          correct_answer_ar: "",
        });
        setNewQ({
          question: "", option_a: "", option_b: "", option_c: "",
          correct_answer: "", hint: "", level: "", hall: "",
          question_ar: "", option_a_ar: "", option_b_ar: "", option_c_ar: "",
          correct_answer_ar: "", hint_ar: "", hall_ar: ""
        });
        if (selectedLevel && payload.level === selectedLevel) {
          fetchQuestions(selectedLevel);
        }
      } else {
        notify(setAddQError, "Unexpected response while adding question.");
      }
    } catch (err) {
      console.error(err);
      notify(setAddQError, "Server error while adding question.");
    }
  };

  // فتح نموذج التحديث
  const openUpdateForm = async (q) => {
    try {
      setSelectedQuestionId(q.id);
      const { data } = await axios.get(`${BASE_URL}/game/questions/id/${q.id}`);
      if (!data?.success || !data?.data) {
        notify(setUpdateError, 'Failed to load question details.');
        return;
      }
      const row = data.data;

      setUpdateQ({
        // EN
        question: row.question || "",
        option_a: row.option_a || "",
        option_b: row.option_b || "",
        option_c: row.option_c || "",
        correct_answer: row.correct_answer || "",
        hint: row.hint || "",
        // AR
        question_ar: row.question_ar || "",
        option_a_ar: row.option_a_ar || "",
        option_b_ar: row.option_b_ar || "",
        option_c_ar: row.option_c_ar || "",
        correct_answer_ar: row.correct_answer_ar || "",
        hint_ar: row.hint_ar || "",
        // مشتركة
        level: row.level || selectedLevel || "",
        hall: row.hall || "",
        hall_ar: row.hall_ar || "" // 👈 نجلبه إن كان راجع من الـ API
      });

      const questionLevel = row.level || selectedLevel || "";
      if (questionLevel) {
        await fetchHallsByLevel(questionLevel);
      }

      setShowUpdateForm(true);
      setTimeout(() => {
        const el = document.getElementById('update-question-field');
        if (el) el.focus();
      }, 0);
    } catch (e) {
      console.error(e);
      notify(setUpdateError, 'Error loading question.');
    }
  };

  const resetUpdateForm = () => {
    setSelectedQuestionId(null);
    setUpdateQ({
      question: "", option_a: "", option_b: "", option_c: "",
      correct_answer: "", hint: "",
      question_ar: "", option_a_ar: "", option_b_ar: "", option_c_ar: "",
      correct_answer_ar: "", hint_ar: "",
      level: "", hall: "", hall_ar: ""
    });
    setShowUpdateForm(false);
  };

  const submitUpdateQuestion = async (e) => {
    e.preventDefault();
    if (!selectedQuestionId) return;

    // تحقق أساسي
    if (!updateQ.question.trim() || !updateQ.option_a.trim() || !updateQ.option_b.trim()
      || !updateQ.option_c.trim() || !updateQ.level || !updateQ.hall) {
      notify(setUpdateError, "Required fields are missing.");
      return;
    }
    if (updateQ.correct_answer && !["A","B","C","D"].includes(String(updateQ.correct_answer).toUpperCase())) {
      notify(setUpdateError, "Correct answer must be A, B, C, or D.");
      return;
    }

    const hallNameUpd = (updateQ.hall || "").trim();
    if (!hallNameUpd) {
      notify(setUpdateError, "Hall is required.");
      return;
    }

    const payload = {
      question: updateQ.question,
      option_a: updateQ.option_a,
      option_b: updateQ.option_b,
      option_c: updateQ.option_c,
      correct_answer: updateQ.correct_answer ? String(updateQ.correct_answer).toUpperCase() : undefined,
      hint: updateQ.hint,
      level: updateQ.level,
      hall: hallNameUpd,
      // العربية (اختياري)
      ...(updateQ.question_ar ? { question_ar: updateQ.question_ar } : {}),
      ...(updateQ.option_a_ar ? { option_a_ar: updateQ.option_a_ar } : {}),
      ...(updateQ.option_b_ar ? { option_b_ar: updateQ.option_b_ar } : {}),
      ...(updateQ.option_c_ar ? { option_c_ar: updateQ.option_c_ar } : {}),
      ...(updateQ.correct_answer_ar ? { correct_answer_ar: updateQ.correct_answer_ar } : {}),
      ...(updateQ.hint_ar ? { hint_ar: updateQ.hint_ar } : {}),
      ...(updateQ.hall_ar ? { hall_ar: updateQ.hall_ar } : {}) // 👈 نرسلها فقط إذا موجودة
    };

    try {
      await axios.put(`${BASE_URL}/game/questions/${selectedQuestionId}`, payload);
      notify(setUpdateSuccess, "Question updated successfully.");
      if (selectedLevel) fetchQuestions(selectedLevel);
      setTimeout(() => {
        setUpdateSuccess("");
        resetUpdateForm();
      }, 800);
    } catch (err) {
      console.error(err);
      notify(setUpdateError, "Error updating question.");
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!questionId) return;

    const confirmed = window.confirm("Are you sure you want to delete this question?");
    if (!confirmed) return;

    try {
      await axios.delete(`${BASE_URL}/game/questions/${questionId}`);
      notify(setQSuccess, "Question deleted successfully.");
      if (showUpdateForm && selectedQuestionId === questionId) {
        resetUpdateForm();
      }
      if (selectedLevel) {
        fetchQuestions(selectedLevel);
      } else {
        setQuestions((prev) => prev.filter((question) => question.id !== questionId));
      }
    } catch (error) {
      console.error(error);
      notify(setQError, "Error deleting question.");
    }
  };

  useEffect(() => { fetchLevels(); /* eslint-disable-next-line */ }, [lang]);

  const newHallOptions = getHallOptions(newQ.level, newQ.hall, newQ.hall_ar);
  const updateHallOptions = getHallOptions(updateQ.level, updateQ.hall, updateQ.hall_ar);

  return (
    <div className="content">
      {/* رأس الصفحة */}
      <Row className="mb-3">
        <Col md="12" className="d-flex align-items-center justify-content-between">
          <h3 className="mb-0">Game Questions</h3>
          <div className="d-flex align-items-center">
            <Label className="mb-0 mr-2">Display Language:</Label>
            <Input
              type="select"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              style={{ width: 140 }}
            >
              <option value="en">EN</option>
              <option value="ar">AR</option>
            </Input>
          </div>
        </Col>
      </Row>

      {/* Add Question */}
      <Form onSubmit={submitNewQuestion}>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Add Question</CardTitle>
              </CardHeader>
              <CardBody>
                {addQError && <div className="alert alert-danger">{addQError}</div>}
                {addQSuccess && <div className="alert alert-success">{addQSuccess}</div>}
                {hallsError && <div className="alert alert-warning">{hallsError}</div>}

                <Row form>
                  <Col md="6">
                    <FormGroup>
                      <Label>Level</Label>
                      <Input
                        type="select"
                        value={newQ.level}
                        onChange={(e) => handleNewLevelChange(e.target.value)}
                      >
                        <option value="">Select level...</option>
                        {levels.map((lv) => (
                          <option key={lv.id || lv.name} value={lv.name}>
                            {lv.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md="6">
                    <FormGroup>
                      <Label>Hall</Label>
                      <Input
                        type="select"
                        value={newQ.hall}
                        onChange={(e) => handleNewHallChange(e.target.value)}
                        disabled={!newQ.level || newHallOptions.length === 0}
                      >
                        <option value="">
                          {!newQ.level
                            ? "Select level first..."
                            : newHallOptions.length === 0
                              ? "No halls configured for this level"
                              : "Select hall..."}
                        </option>
                        {newHallOptions.map((hallItem) => (
                          <option key={`${newQ.level}-${hallItem.hall}`} value={hallItem.hall}>
                            {getHallLabel(hallItem)}
                          </option>
                        ))}
                      </Input>
                      {newQ.level && newHallOptions.length === 0 && (
                        <small className="text-muted">
                          Add halls for this level from Game Hall Order first.
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label>Question</Label>
                  <Input
                    type="text"
                    required
                    value={newQ.question}
                    onChange={(e) => setNewQ({ ...newQ, question: e.target.value })}
                  />
                </FormGroup>

                <Row form>
                  <Col md="4">
                    <FormGroup>
                      <Label>Option A</Label>
                      <Input
                        type="text"
                        required
                        value={newQ.option_a}
                        onChange={(e) => setNewQ({ ...newQ, option_a: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Option B</Label>
                      <Input
                        type="text"
                        required
                        value={newQ.option_b}
                        onChange={(e) => setNewQ({ ...newQ, option_b: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Option C</Label>
                      <Input
                        type="text"
                        required
                        value={newQ.option_c}
                        onChange={(e) => setNewQ({ ...newQ, option_c: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row form>
                  <Col md="6">
                    <FormGroup>
                      <Label>Correct Answer (A/B/C)</Label>
                      <Input
                        type="text"
                        required
                        maxLength={1}
                        value={newQ.correct_answer}
                        onChange={(e) =>
                          {
                            setNewQ({
                              ...newQ,
                              correct_answer: e.target.value.toUpperCase().replace(/\s+/g, "")
                            });
                            setNewQFieldErrors((prev) => ({ ...prev, correct_answer: "" }));
                          }
                        }
                      />
                      {newQFieldErrors.correct_answer && (
                        <small style={{ color: "red", display: "block", marginTop: 6 }}>
                          {newQFieldErrors.correct_answer}
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Hint</Label>
                      <Input
                        type="text"
                        required
                        value={newQ.hint}
                        onChange={(e) => setNewQ({ ...newQ, hint: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Card className="mt-3">
                  <CardHeader><CardTitle tag="h5">Arabic Fields</CardTitle></CardHeader>
                  <CardBody>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>اسم القاعة (ع)</Label>
                          <Input
                            type="text"
                            value={newQ.hall_ar}
                            readOnly
                            placeholder="Selected automatically from the chosen hall"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup>
                      <Label>السؤال (ع)</Label>
                      <Input
                        type="text"
                        required
                        value={newQ.question_ar}
                        onChange={(e) => setNewQ({ ...newQ, question_ar: e.target.value })}
                      />
                    </FormGroup>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label>الخيار أ</Label>
                          <Input
                            type="text"
                            required
                            value={newQ.option_a_ar}
                            onChange={(e) => setNewQ({ ...newQ, option_a_ar: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label>الخيار ب</Label>
                          <Input
                            type="text"
                            required
                            value={newQ.option_b_ar}
                            onChange={(e) => setNewQ({ ...newQ, option_b_ar: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label>الخيار ج</Label>
                          <Input
                            type="text"
                            required
                            value={newQ.option_c_ar}
                            onChange={(e) => setNewQ({ ...newQ, option_c_ar: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label>الإجابة الصحيحة (أ/ب/ج)</Label>
                          <Input
                            type="text"
                            required
                            maxLength={1}
                            value={newQ.correct_answer_ar}
                            onChange={(e) =>
                              {
                                setNewQ({
                                  ...newQ,
                                  correct_answer_ar: e.target.value.replace(/\s+/g, "")
                                });
                                setNewQFieldErrors((prev) => ({ ...prev, correct_answer_ar: "" }));
                              }
                            }
                          />
                          {newQFieldErrors.correct_answer_ar && (
                            <small style={{ color: "red", display: "block", marginTop: 6 }}>
                              {newQFieldErrors.correct_answer_ar}
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label>تلميح</Label>
                          <Input
                            type="text"
                            required
                            value={newQ.hint_ar}
                            onChange={(e) => setNewQ({ ...newQ, hint_ar: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </CardBody>
              <CardFooter>
                <Button color="primary" type="submit" className="btn-round">Add Question</Button>
              </CardFooter>
            </Card>
          </Col>
        </Row>
      </Form>

      {/* Levels + Load Questions */}
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h4" className="mb-0">Questions</CardTitle>
              <div>
                <Label className="mr-2 mb-0">Level:</Label>
                <Input
                  type="select"
                  value={selectedLevel}
                  onChange={(e) => setSelectedLevel(e.target.value)}
                  style={{ width: 220, display: "inline-block", marginRight: 10 }}
                >
                  <option value="">Select level...</option>
                  {levels.map((lv) => (
                    <option key={lv.id || lv.name} value={lv.name}>
                      {lv.label}
                    </option>
                  ))}
                </Input>
                <Button color="primary" onClick={() => fetchQuestions(selectedLevel)}>Load</Button>
              </div>
            </CardHeader>

            <CardBody>
              {levelsError && <div className="alert alert-danger">{levelsError}</div>}
              {qError && <div className="alert alert-danger">{qError}</div>}
              {qSuccess && <div className="alert alert-success">{qSuccess}</div>}
              {qLoading ? (
                <div>Loading...</div>
              ) : (
                <Table responsive>
                  <thead className="text-primary">
                    <tr>
                      <th>#</th>
                      <th>Question</th>
                      <th>Options</th>
                      <th>Correct</th>
                      <th>Hint</th>
                      <th>Hall</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.length === 0 ? (
                      <tr><td colSpan="7">No questions.</td></tr>
                    ) : questions.map((q, idx) => (
                      <tr key={q.id ?? idx}>
                        <td>{idx + 1}</td>
                        <td>{q.question}</td>
                        <td>
                          {Object.entries(q.options || {}).map(([k, v]) => (
                            <div key={k}><strong>{k}:</strong> {v}</div>
                          ))}
                        </td>
                        <td>{q.correctAnswer}</td>
                        <td>{q.hint || "-"}</td>
                        <td>{q.hall}</td>
                        <td>
                          <Button color="success" style={{ marginRight: 8 }} onClick={() => openUpdateForm(q)}>
                            Update
                          </Button>
                          <Button color="danger" onClick={() => handleDeleteQuestion(q.id)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
            <CardFooter>
              <div>Total Questions Loaded: {questions.length}</div>
            </CardFooter>
          </Card>
        </Col>
      </Row>

      {/* Update Form */}
      {showUpdateForm && (
        <div className="content">
          <Form onSubmit={submitUpdateQuestion}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Level</Label>
                  <Input
                    type="select"
                    value={updateQ.level}
                    onChange={(e) => handleUpdateLevelChange(e.target.value)}
                  >
                    <option value="">Select level...</option>
                    {levels.map((lv) => (
                      <option key={lv.id || lv.name} value={lv.name}>
                        {lv.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>Hall</Label>
                  <Input
                    type="select"
                    value={updateQ.hall}
                    onChange={(e) => handleUpdateHallChange(e.target.value)}
                    disabled={!updateQ.level || updateHallOptions.length === 0}
                  >
                    <option value="">
                      {!updateQ.level
                        ? "Select level first..."
                        : updateHallOptions.length === 0
                          ? "No halls configured for this level"
                          : "Select hall..."}
                    </option>
                    {updateHallOptions.map((hallItem) => (
                      <option key={`${updateQ.level}-${hallItem.hall}`} value={hallItem.hall}>
                        {getHallLabel(hallItem)}
                      </option>
                    ))}
                  </Input>
                  {updateQ.level && updateHallOptions.length === 0 && (
                    <small className="text-muted">
                      Add halls for this level from Game Hall Order first.
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>Question</Label>
                  <Input
                    id="update-question-field"
                    type="text"
                    value={updateQ.question}
                    onChange={(e) => setUpdateQ({ ...updateQ, question: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="4">
                <FormGroup>
                  <Label>Option A</Label>
                  <Input
                    type="text"
                    value={updateQ.option_a}
                    onChange={(e) => setUpdateQ({ ...updateQ, option_a: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>Option B</Label>
                  <Input
                    type="text"
                    value={updateQ.option_b}
                    onChange={(e) => setUpdateQ({ ...updateQ, option_b: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>Option C</Label>
                  <Input
                    type="text"
                    value={updateQ.option_c}
                    onChange={(e) => setUpdateQ({ ...updateQ, option_c: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Correct Answer (A/B/C)</Label>
                  <Input
                    type="text"
                    value={updateQ.correct_answer}
                    onChange={(e) => setUpdateQ({ ...updateQ, correct_answer: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Hint</Label>
                  <Input
                    type="text"
                    value={updateQ.hint}
                    onChange={(e) => setUpdateQ({ ...updateQ, hint: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* حقول عربية اختيارية */}
            <Card className="mt-3">
              <CardHeader><CardTitle tag="h5">Arabic Fields</CardTitle></CardHeader>
              <CardBody>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>اسم القاعة (ع)</Label>
                      <Input
                        type="text"
                        value={updateQ.hall_ar}
                        readOnly
                        placeholder="Selected automatically from the chosen hall"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label>السؤال (ع)</Label>
                  <Input
                    type="text"
                    value={updateQ.question_ar}
                    onChange={(e) => setUpdateQ({ ...updateQ, question_ar: e.target.value })}
                  />
                </FormGroup>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label>الخيار أ</Label>
                      <Input
                        type="text"
                        value={updateQ.option_a_ar}
                        onChange={(e) => setUpdateQ({ ...updateQ, option_a_ar: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>الخيار ب</Label>
                      <Input
                        type="text"
                        value={updateQ.option_b_ar}
                        onChange={(e) => setUpdateQ({ ...updateQ, option_b_ar: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>الخيار ج</Label>
                      <Input
                        type="text"
                        value={updateQ.option_c_ar}
                        onChange={(e) => setUpdateQ({ ...updateQ, option_c_ar: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>الإجابة الصحيحة (أ/ب/ج)</Label>
                      <Input
                        type="text"
                        value={updateQ.correct_answer_ar}
                        onChange={(e) => setUpdateQ({ ...updateQ, correct_answer_ar: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>تلميح</Label>
                      <Input
                        type="text"
                        value={updateQ.hint_ar}
                        onChange={(e) => setUpdateQ({ ...updateQ, hint_ar: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Row className="mt-3">
              <Col md="12">
                <div className="update ml-auto mr-auto">
                  <Button className="btn-round" color="primary" type="submit">
                    Update Question
                  </Button>{" "}
                  <Button
                    className="btn-round"
                    color="secondary"
                    onClick={resetUpdateForm}
                  >
                    Cancel
                  </Button>

                  {updateSuccess && (
                    <div className="alert alert-success" role="alert" style={{ width: '50%', margin: '10px auto', textAlign: 'center' }}>
                      {updateSuccess}
                    </div>
                  )}
                  {updateError && (
                    <div className="alert alert-danger" role="alert" style={{ width: '50%', margin: '10px auto', textAlign: 'center' }}>
                      {updateError}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </div>
  );
}

export default GameQuestions;
